import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import type { SortingState } from "@tanstack/react-table";
import { Passport, Trash02 } from "@untitledui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { Avatar } from "@/components/base/avatar/avatar";
import { Button } from "@/components/base/buttons/button";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { EmptyValue } from "@/components/common";
import CopyableId from "@/components/Molecules/CopyableId/CopyableId";
import type { ColumnDef } from "@/components/Molecules/Table";
import { CustomTableWrapper } from "@/components/Molecules/Table/CustomTableWrapper";
import { getActionsColumnHeader } from "@/components/Molecules/Table/table-column-headers";
import { CreateOrganizationRoleModal } from "@/components/Organisms/CreateOrganizationRoleModal/CreateOrganizationRoleModal";
import { PageHeader } from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { Illustration } from "@/components/shared-assets/illustrations";
import { DeleteOrganizationRoleModal } from "@/features/organization-roles/DeleteOrganizationRoleModal";
import { RoleSourceValue } from "@/features/organization-roles/RoleSourceValue";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useIndexedValidActions } from "@/hooks/use-indexed-valid-actions";
import { useTableSettings } from "@/hooks/use-table-settings";
import { authQueryOptions } from "@/services/auth";
import type { OrganizationRoleListItem } from "@/services/organization";
import { organizationQueryOptions } from "@/services/organization";

const TABLE_KEY = "org-admin.roles";
const DEFAULT_PAGE_SIZE = 10;

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/roles",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/roles",
	});
	const canCreateRoleQuery = useQuery(
		authQueryOptions.checkAction({
			name: "CreateOrganizationRole",
			entityId: params.orgId,
		}),
	);
	const canCreateRole = canCreateRoleQuery.data ?? false;
	const [isCreateOpen, setIsCreateOpen] = useState(false);

	return (
		<>
			<PageLayout
				header={
					<PageHeader
						type="landing"
						title={t("org-admin.roles.table.title")}
						description={t("org-admin.roles.table.description")}
						actions={
							canCreateRole
								? [
										{
											label: t("org-admin.roles.table.createRole"),
											color: "accent",
											testId: "org-role-table-create-btn",
											onPress: () => setIsCreateOpen(true),
										},
									]
								: undefined
						}
					/>
				}
			>
				<RoleTableComponent />
			</PageLayout>
			<CreateOrganizationRoleModal
				isOpen={isCreateOpen}
				organizationId={params.orgId}
				onOpenChange={setIsCreateOpen}
			/>
		</>
	);
}

function RoleTableComponent() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();

	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/roles",
	});
	const openRoleId = useParams({ strict: false }).roleId;
	// Track current page for manual pagination
	const [pageIndex, setPageIndex] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [sorting, setSorting] = useState<SortingState>([]);

	// Track if persisted settings have been applied
	const hasAppliedPersistedSettings = useRef(false);

	// Table settings persistence (page size only)
	const {
		settings: tableSettings,
		isLoaded: settingsLoaded,
		savePageSize,
	} = useTableSettings({
		tableKey: TABLE_KEY,
		defaultPageSize: DEFAULT_PAGE_SIZE,
	});

	// Apply persisted settings (page size) when config has loaded
	useEffect(() => {
		if (!settingsLoaded || hasAppliedPersistedSettings.current) return;
		hasAppliedPersistedSettings.current = true;
		if (
			Number.isFinite(tableSettings.pageSize) &&
			[10, 25, 50, 100].includes(tableSettings.pageSize)
		) {
			setPageSize(tableSettings.pageSize);
		}
	}, [settingsLoaded, tableSettings.pageSize]);

	// Fetch all global roles (includes both Platform and Organization roles)
	const {
		data: organizationRoles = [],
		isLoading,
		isFetching,
		isError,
		error,
		refetch,
	} = useQuery(organizationQueryOptions.roles.list(params.orgId));

	const [roleToDelete, setRoleToDelete] =
		useState<OrganizationRoleListItem | null>(null);
	const deleteActions = useMemo(
		() =>
			organizationRoles.map((role) => ({
				name: "DeleteOrganizationRole",
				entityType: "OrganizationRole",
				entityId: role.id,
			})),
		[organizationRoles],
	);
	// Role assignments change on the Users page, so never reuse an earlier result.
	const { allowed: deleteAllowed } = useIndexedValidActions(
		deleteActions,
		true,
		{ staleTime: 0, gcTime: 0 },
	);
	const deleteAllowedById = useMemo(() => {
		const allowed = new Map<string, boolean>();
		organizationRoles.forEach((role, index) => {
			allowed.set(role.id, deleteAllowed[index] === true);
		});
		return allowed;
	}, [organizationRoles, deleteAllowed]);
	const showActionsColumn = deleteAllowed.some(Boolean);

	// Apply client-side pagination
	const data = useMemo(() => {
		const startIndex = pageIndex * pageSize;
		const endIndex = startIndex + pageSize;
		const userSort = sorting[0]?.id === "users" ? sorting[0] : undefined;
		const ranked = userSort
			? [...organizationRoles].sort((a, b) => {
					const difference =
						(a.activeUserCount ?? 0) - (b.activeUserCount ?? 0);
					return userSort.desc ? -difference : difference;
				})
			: organizationRoles;
		return ranked.slice(startIndex, endIndex);
	}, [organizationRoles, pageIndex, pageSize, sorting]);

	// Calculate total pages for pagination controls
	const pageCount = useMemo(() => {
		if (!organizationRoles.length) return 0;
		return Math.ceil(organizationRoles.length / pageSize);
	}, [organizationRoles.length, pageSize]);

	const handlePaginationChange = ({
		pageSize: newPageSize,
		pageIndex: newPageIndex,
	}: {
		pageSize: number;
		pageIndex: number;
	}) => {
		if (newPageSize !== pageSize) {
			setPageSize(newPageSize);
			setPageIndex(0); // Reset to first page when size changes
			savePageSize(newPageSize);
		} else if (newPageIndex !== pageIndex) {
			setPageIndex(newPageIndex);
		}
	};

	const createBasicColumns: ColumnDef<OrganizationRoleListItem>[] = [
		{
			id: "roleName",
			accessorKey: "name",
			header: t("admin.roles.table.columns.name"),
			enableSorting: true,
			meta: {
				headerProps: {
					isRowHeader: true,
				},
			},
			cell: ({ row }) => {
				const role = row.original;
				return (
					<div className="flex items-center gap-3">
						<Avatar
							alt={role.name ?? ""}
							placeholder={<Passport className="size-6 text-gray-400" />}
							size="md"
						/>
						<div className="whitespace-nowrap">
							<p className="text-sm font-medium text-primary">
								{role.name ?? <EmptyValue />}
							</p>
						</div>
					</div>
				);
			},
		},
		{
			id: "description",
			accessorKey: "description",
			header: t("admin.roles.table.columns.description"),
			enableSorting: true,
			cell: ({ row }) => {
				return row.original.description ? (
					<span className="text-sm font-medium text-primary">
						{row.original.description}
					</span>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "source",
			accessorFn: (role) => role.custom,
			header: t("admin.roles.table.columns.source"),
			enableSorting: true,
			cell: ({ row }) => <RoleSourceValue custom={row.original.custom} />,
		},
		{
			id: "users",
			accessorKey: "activeUserCount",
			header: t("admin.roles.table.columns.users"),
			enableSorting: true,
			cell: ({ row }) => (
				<span className="text-sm font-medium text-primary">
					{row.original.activeUserCount ?? 0}
				</span>
			),
		},
		{
			id: "id",
			accessorKey: "id",
			header: t("admin.roles.table.columns.id"),
			enableSorting: true,
			cell: ({ row }) => {
				return row.original.id ? (
					<CopyableId value={row.original.id} />
				) : (
					<EmptyValue />
				);
			},
		},
	];

	// Handle refresh with loading state
	const [isRefreshing, setIsRefreshing] = useState(false);
	const handleRefresh = async () => {
		setIsRefreshing(true);
		try {
			// Refetch current page
			await refetch();
		} finally {
			setIsRefreshing(false);
		}
	};

	const actionsColumn: ColumnDef<OrganizationRoleListItem> = {
		id: "actions",
		header: getActionsColumnHeader(t),
		enableSorting: false,
		enableHiding: false,
		size: 56,
		meta: {
			disableColumnReorder: true,
			cellProps: {
				className: "w-14 px-2",
				"data-suppress-row-click": true,
			},
		},
		cell: ({ row }) => {
			const role = row.original;
			if (deleteAllowedById.get(role.id) !== true) return null;
			return (
				<div className="flex justify-end" data-suppress-row-click>
					<Dropdown.Root>
						<Dropdown.DotsButton
							data-testid="org-role-table-row-actions-btn"
							aria-label={t("common.tableActions.options")}
						/>
						<Dropdown.Popover className="w-min">
							<Dropdown.Menu>
								<Dropdown.Item
									id="delete"
									data-testid="org-role-table-action-delete"
									label={t("admin.roles.delete.action")}
									icon={Trash02}
									className="[&_span]:text-red-600"
									onAction={() => setRoleToDelete(role)}
								/>
							</Dropdown.Menu>
						</Dropdown.Popover>
					</Dropdown.Root>
				</div>
			);
		},
	};

	const roleColumns = showActionsColumn
		? [...createBasicColumns, actionsColumn]
		: createBasicColumns;

	const handleRowClick = (role: OrganizationRoleListItem) => {
		navigate({
			to: "/org-admin/$orgId/roles/$roleId/details",
			params: {
				orgId: params.orgId,
				roleId: role.id,
			},
		});
	};

	// Handle error state
	if (isError) {
		return (
			<div className="space-y-0">
				<div className="flex flex-col items-center justify-center py-12">
					<div className="text-center">
						<h3 className="text-sm font-medium text-error">
							{t("admin.roles.table.errors.failedToLoadRoles")}
						</h3>
						<p className="text-sm text-tertiary mt-1">{error?.message}</p>
						<Button
							size="sm"
							color="primary"
							onClick={handleRefresh}
							className="mt-3"
						>
							{t("common.actions.tryAgain")}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	const noRoles = () => {
		return (
			<div className="flex flex-col items-center justify-center">
				<div className="relative flex items-center justify-center w-[480px] h-[480px]">
					<div className="relative flex flex-col items-center justify-center">
						<Illustration type="cloud" size="sm">
							<Passport className="size-6" />
						</Illustration>
						<div className="mt-4 text-center">
							<h2 className="font-semibold text-primary text-md">
								{t("admin.roles.table.errors.noRoles")}
							</h2>
							<p className="text-sm text-tertiary">
								{t("admin.roles.table.errors.rolesWillAppearHere")}
							</p>
						</div>
					</div>
				</div>
			</div>
		);
	};

	return (
		<>
			<CustomTableWrapper
				tableKey={TABLE_KEY}
				data={data}
				columns={roleColumns}
				enableSorting={true}
				onSortingChange={(next) => {
					setSorting(next);
					setPageIndex(0);
				}}
				// enableGlobalFilter={false}
				onRowClick={handleRowClick}
				// Enhanced loading states
				isLoading={isLoading}
				isRefreshing={isRefreshing}
				onRefresh={handleRefresh}
				// Manual pagination
				paginationMode="server"
				externalPaginationState={{
					pageSize,
					pageIndex,
				}}
				onPaginationChange={handlePaginationChange}
				pageCount={pageCount}
				rowCount={organizationRoles.length}
				totalRowCount={organizationRoles.length}
				// showRefreshButton={true}
				// Existing props
				showCard={true}
				showHeaderPadding={false}
				zebraStriping
			/>

			{!isLoading && !isFetching && (data?.length ?? 0) === 0
				? noRoles()
				: null}

			<Outlet />
			<DeleteOrganizationRoleModal
				isOpen={roleToDelete != null}
				onOpenChange={(open) => {
					if (!open) setRoleToDelete(null);
				}}
				organizationId={params.orgId}
				roleId={roleToDelete?.id ?? ""}
				roleName={roleToDelete?.name ?? ""}
				onDeleted={() => {
					if (roleToDelete?.id === openRoleId) {
						navigate({
							to: "/org-admin/$orgId/roles",
							params: { orgId: params.orgId },
							replace: true,
						});
					}
				}}
			/>
		</>
	);
}
